import { toolsRegistry, ToolEntry } from "./toolsRegistry";
import { evaluateIntent, UnifiedToolLike } from "../shared/intents/intentMatcher";
import { toolsMetadata } from "../shared/intents/toolsMetadata";

export type UnifiedActionType = 'cta' | 'draft';

export interface UnifiedAction {
  actionType: UnifiedActionType;
  toolId: string;
  toolName: string;
  score: number;
  text: string; // assistant text to show
  ctaLabel?: string;
  route?: string;
  payload?: Record<string, any>;
}

// Lightweight normalization helpers
const STOPWORDS = new Set([
  "the","a","an","to","for","of","and","or","in","my","our","your","with","on","at","by","from","about","into","over","after"
]);

const SYNONYMS: Record<string, string[]> = {
  // website
  website: ["site", "web site", "homepage", "landing page", "pages"],
  build: ["create", "make", "generate", "setup", "set up", "produce"],
  generator: ["builder", "maker", "creator"],
  image: ["picture", "photo", "graphic", "visual"],
  video: ["clip", "short", "reel"],
  social: ["facebook", "linkedin", "twitter", "x", "instagram", "threads"],
  seo: ["search", "rank", "ranking", "organic"],
  email: ["nurture", "drip", "newsletter"],
  leads: ["lead", "pipeline", "mql", "prospect"],
};

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter(w => !STOPWORDS.has(w));
}

function expandTriggers(base: string[]): string[] {
  const out = new Set<string>();
  for (const trig of base) {
    out.add(trig.toLowerCase());
    const parts = tokenize(trig);
    for (const p of parts) {
      if (SYNONYMS[p]) {
        for (const s of SYNONYMS[p]) out.add(s);
      }
    }
  }
  return Array.from(out);
}

function keywordScore(text: string, triggers: string[], tool?: ToolEntry): number {
  const t = text.toLowerCase();
  let score = 0;
  const expanded = expandTriggers(triggers);

  // Include keywords from tool name as light signals
  if (tool?.name) {
    const nameTokens = tokenize(tool.name).filter(w => !STOPWORDS.has(w));
    for (const n of nameTokens) expanded.push(n);
  }

  // Score phrases and keywords
  const matched = new Set<string>();
  for (const kRaw of expanded) {
    const k = kRaw.toLowerCase();
    if (!k) continue;
    if (k.includes(" ")) {
      if (t.includes(k)) { score += 2; matched.add(k); }
    } else {
      if (new RegExp(`\\b${k}\\b`, "i").test(text)) { score += 1; matched.add(k); }
    }
  }

  // Small boost for multiple distinct matches
  if (matched.size >= 2) score += 0.5;
  if (matched.size >= 3) score += 0.5; // cumulative

  // Light bonus if tool name appears directly in text
  if (tool?.name && t.includes(tool.name.toLowerCase())) score += 0.5;

  // Light bonus if description contains any matched keyword and text also mentions it
  if (tool?.description) {
    const desc = tool.description.toLowerCase();
    matched.forEach((m) => {
      if (desc.includes(m)) { score += 0.2; }
    });
  }

  return score;
}

export function evaluateLocalActions(message: string): UnifiedAction | null {
  if (!message || typeof message !== "string") return null;
  const lower = message.toLowerCase();

  // Early heuristics: capture common natural requests before scoring
  // Example: "can you build me a website", "help me create a website", etc.
  if (
    /(create|make|generate|design)\s+(a\s+)?logo\b/i.test(lower) ||
    /\blogo\s+generator\b/i.test(lower) ||
    /\bbrandmark\b/i.test(lower)
  ) {
    const logoTool = toolsRegistry.find((t) => t.id === "marketing.logo.generate");
    if (logoTool) {
      return {
        actionType: 'cta',
        toolId: logoTool.id,
        toolName: logoTool.name,
        score: 99,
        text: `I can open ${logoTool.name} to help with that.`,
        ctaLabel: logoTool.route ? `Open ${logoTool.name}` : `Use ${logoTool.name}`,
        route: logoTool.route,
      };
    }
  }

  if (/(build|create|make|setup|set up).*(website|site|homepage|landing\s?page)/i.test(lower)) {
    const websiteTool = toolsRegistry.find(t => t.id === "marketing.ai_website");
    if (websiteTool) {
      return {
        actionType: 'cta',
        toolId: websiteTool.id,
        toolName: websiteTool.name,
        score: 99, // force top ranking for this explicit heuristic
        text: `I can open ${websiteTool.name} to help with that.`,
        ctaLabel: websiteTool.route ? `Open ${websiteTool.name}` : `Use ${websiteTool.name}`,
        route: websiteTool.route,
      };
    }
  }

  // Early heuristic: creating a social post (avoid misrouting to email)
  // Examples: "create a post about our new email", "write a LinkedIn post announcing we sent the email"
  if (/(create|write|draft|compose|make)\s+.*\b(post|linkedin|social|tweet|twitter|instagram|threads|x)\b/i.test(lower) || /\bpost\b\s+.*\babout\b/i.test(lower)) {
    const postTool = toolsRegistry.find(t => t.id === "marketing.social_posts");
    if (postTool) {
      const platform = /(linkedin|ln)/i.test(lower)
        ? 'linkedin'
        : /(twitter|tweet|x)/i.test(lower)
        ? 'twitter'
        : /(instagram)/i.test(lower)
        ? 'instagram'
        : 'generic';
      let topic = message
        .replace(/^(please|can you|could you)\s*/i, '')
        .replace(/(create|write|draft|compose|make)\s+(a\s+)?/i, '')
        .replace(/\b(linkedin|ln|post|tweet|twitter|instagram|social)\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
      return {
        actionType: 'draft',
        toolId: postTool.id,
        toolName: postTool.name,
        score: 98.5,
        text: `I'll draft a ${platform === 'generic' ? 'social' : platform} post for you.`,
        route: postTool.route,
        payload: { platform, topic },
      };
    }
  }

  if (/(create|launch|start|build)\s+.*\b(telemarketing|cold\s*call|cold\s*calling|outbound\s*call|calling\s+campaign|ai\s+caller|book\s+meetings|book\s+demos)\b/i.test(lower)) {
    const tmTool = toolsRegistry.find(t => t.id === "crm.telemarketing.book_meetings_campaign");
    if (tmTool) {
      return {
        actionType: 'cta',
        toolId: tmTool.id,
        toolName: tmTool.name,
        score: 99,
        text: `I can set up an AI telemarketing campaign to book meetings.`,
        ctaLabel: tmTool.route ? `Set Up ${tmTool.name}` : `Use ${tmTool.name}`,
        route: tmTool.route,
      };
    }
  }

  if (/(create|launch|start|build)\s+.*\b(cold\s+email\s+campaign|email\s+sequence|outreach\s+campaign|email\s+campaign)\b/i.test(lower)) {
    const campaignTool = toolsRegistry.find(t => t.id === "marketing.cold_email.create_campaign");
    if (campaignTool) {
      return {
        actionType: 'draft',
        toolId: campaignTool.id,
        toolName: campaignTool.name,
        score: 99,
        text: `I'll help you create a cold email campaign with AI-generated copy.`,
        route: campaignTool.route,
        payload: { action: 'create_campaign' }
      };
    }
  }

  // Early heuristic: sending an email (intent-based action)
  // Matches phrases like: "send an email", "compose email to", "write email to john@..."
  if (/(send|compose|write)\s+(an\s+)?email(\s+to\s+|\s+)/i.test(lower) && !/\b(post|linkedin|social|tweet|twitter|instagram|threads|x)\b/i.test(lower)) {
    const emailTool = toolsRegistry.find(t => t.id === "communications.email.send");
    if (emailTool) {
      return {
        actionType: 'cta',
        toolId: emailTool.id,
        toolName: emailTool.name,
        score: 98,
        text: `I can send an email using ${emailTool.name}.`,
        ctaLabel: emailTool.route ? `Open ${emailTool.name}` : `Use ${emailTool.name}`,
        route: emailTool.route,
      };
    }
  }

  const tools: UnifiedToolLike[] = toolsMetadata.map((t) => ({
    id: t.id,
    name: t.name,
    description: t.description,
    triggers: t.triggers,
    route: t.route,
  }));

  const shared = evaluateIntent(message, tools);
  if (!shared) return null;

  return {
    actionType: shared.actionType,
    toolId: shared.toolId,
    toolName: shared.toolName,
    score: shared.score,
    text: shared.text,
    ctaLabel: shared.ctaLabel,
    route: shared.route,
    payload: shared.payload,
  };
}
